import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// import { supabase } from '../../../supabase';
import './MyCreations.scss';
import { postCreation } from '../../Reducers/actions/creationsActions';
import { capitalizeFirstLetter, getFirstTitleFromHtml } from '../../../helpers/utilities';
import { fetchHeatmapTags } from '../../Reducers/actions/heatmapActions';
import { setTopNavbar } from '../../Reducers/actions/navbarActions';
import useAutoGrowTextarea from '../../hooks/useAutoGrowTextarea';
import { useStripeConnectEmbeddedOnboarding, normalizeKycStatus } from '../../hooks/useStripeConnectEmbeddedOnboarding';
import StripeEmbeddedOnboarding from '../../components/StripeEmbeddedOnboarding/StripeEmbeddedOnboarding';
import Icon from '../../components/Icon/Icon';
import { supabase } from '../../../supabase';
import AddCategory from '../../components/AddCategory/AddCategory';
import './AddCreation.scss';
import NewsEditor from '../../components/NewsEditor/NewsEditor';
import { getFirstParagraphText } from '../../../helpers/utilities';
import CreationCard from '../../components/CreationCard/CreationCard';
import { cachingService } from '../../../helpers/cache';
import ModalMessage from '../../components/ModalMessage/ModalMessage';
import CreationAdded from '../../components/CreationAdded/CreationAdded';
import { uploadFailure, uploadRequest, uploadSuccess, updateUploadProgress } from '../../Reducers/actions/uploadActions';
import { calculateFullPrice } from '../../../helpers/money';
import { buildOnboardingPayloadFromMainAddress } from '../../../helpers/stripeOnboardingPayload';
import PriceDetailsAddCreation from '../../components/PriceDetails/PriceDetailsAddCreation';
import { HYK_FEE_PERCENT, STRIPE_FEE_PERCENT, STRIPE_FIXED_FEE } from '../../constants/globals';
import { uploadFileWithTUS, uploadFileStandard } from '../../../helpers/tusUpload';
import Toggle from '../../components/Toggle/Toggle';
import ShippingMethodSelector from '../../components/ShippingMethodSelector/ShippingMethodSelector';
import { getShippingMethods } from '../../Reducers/actions/shippingMethodsActions';
import useSocialNetworksPrompt from '../../hooks/useSocialNetworksPrompt';
import SocialNetworksPrompt from '../../components/SocialNetworksPrompt/SocialNetworksPrompt';
import { SUPPORTED_TYPES } from '../../../helpers/fileConstants';
import { completeOnboarding, getKycStatus } from '../../Reducers/actions/accountActions';
import { userNeedsEmailVerificationOrPasswordSetup } from '../../constants/signupFlow';

const createEmptySizeEntry = () => ({ size_label: '', stock_quantity: '' });

// This screen treats a KYC status without a confirmed light-KYC completion
// (missing/undefined) as still verified — only an explicit `false` blocks
// publishing. Applied consistently across every KYC read in this file (mount
// fetch, focus refresh, embedded-onboarding exit) so `kycStatus.has_light_kyc`
// always means the same thing regardless of which one last set it.
const deriveAddCreationKycStatus = (data) => {
  const normalized = normalizeKycStatus(data);
  return { ...normalized, has_light_kyc: normalized.has_light_kyc !== false };
};

const AddCreation = () => {
  const navigate = useNavigate();

  const user = useSelector(state => state.auth?.user);
  const session = useSelector(state => state.auth?.session);
  const tags = useSelector(state => state.heatmap?.tags);
  const { methods: shippingMethods } = useSelector(state => state.shippingMethods || { methods: [] });

  const userId = user?.user_id

  const [currentStep, setCurrentStep] = useState(1);
  // Media files state — HYK-570: multiple files + legacy single file
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [error, setError] = useState({});
  const [free, setFree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [isSizingAndQuantitiesEnabled, setIsSizingAndQuantitiesEnabled] = useState(false);
  const [shippingMethodError, setShippingMethodError] = useState(null);
  const dispatch = useDispatch()

  const textareaRef = useRef(null);
  const creditsTextareaRef = useRef(null);
  const [openDesc, setOpenDesc] = useState(false);
  const [my_tags, setMyTags] = useState([]);
  const [typeSet, setTypeSet] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false)
  const [fullPrice, setFullPrice] = useState('')
  const [sizeEntries, setSizeEntries] = useState(() => [createEmptySizeEntry()]);
  const [sizeErrors, setSizeErrors] = useState([]);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState(null);
  const [showShippingMethodSelector, setShowShippingMethodSelector] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [kycStatus, setKycStatus] = useState({ has_light_kyc: true, currently_due: [], account_id: null });
  const [kycStatusLoading, setKycStatusLoading] = useState(false);
  const [kycStatusError, setKycStatusError] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const { connectInstance, initializeEmbeddedOnboarding, handleEmbeddedOnboardingExit: exitEmbeddedOnboarding } =
    useStripeConnectEmbeddedOnboarding();

  // HYK-914 (Music only): implicit mode (single if 1 file, album if 2+)
  const tracksListRef = useRef(null);
  const draggingTrackIdRef = useRef(null);
  const rafReorderRef = useRef(null);

  // Social networks prompt modal (creation publish trigger)
  const socialNetworksPrompt = useSocialNetworksPrompt('creation-publish');

  const blockedFromPublishing =
    userNeedsEmailVerificationOrPasswordSetup(user, session);

  useLayoutEffect(() => {
    if (blockedFromPublishing) {
      navigate('/explore', { replace: true });
    }
  }, [blockedFromPublishing, navigate]);

  useEffect(() => {
    dispatch(setTopNavbar({
      title: 'Add Creation',
      showBackArrow: true
    }))
    if (!(tags && tags.length > 0)) {
      dispatch(fetchHeatmapTags())
    }
    // Fetch shipping methods
    dispatch(getShippingMethods());

    return () => {
      dispatch(setTopNavbar({
        title: '',
        showBackArrow: false
      }))
      // HYK-570: Cleanup blob URLs on unmount
      mediaFiles.forEach(fileItem => {
        if (fileItem.preview) {
          URL.revokeObjectURL(fileItem.preview);
        }
      });
      if (mediaPreview) {
        URL.revokeObjectURL(mediaPreview);
      }
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    }
  }, [])

  useEffect(() => {
    if (!userId) return;
    let isMounted = true;

    const fetchKyc = async () => {
      setKycStatusLoading(true);
      setKycStatusError(null);
      try {
        const response = await getKycStatus();
        if (!isMounted) return;

        if (response?.success && response?.data) {
          setKycStatus(deriveAddCreationKycStatus(response.data));
          return;
        }
        setKycStatus({
          has_light_kyc: false,
          currently_due: [],
          account_id: null,
        });
        setKycStatusError('Unable to verify account status right now.');
      } catch (err) {
        if (!isMounted) return;
        setKycStatus({
          has_light_kyc: false,
          currently_due: [],
          account_id: null,
        });
        setKycStatusError('Unable to verify account status right now.');
      } finally {
        if (isMounted) setKycStatusLoading(false);
      }
    };

    fetchKyc();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    const handleWindowFocus = async () => {
      // Stripe may open hosted steps in a new tab; refresh status when user comes back.
      try {
        const response = await getKycStatus();
        if (response?.success && response?.data) {
          setKycStatus(deriveAddCreationKycStatus(response.data));
          setKycStatusError(null);
        }
      } catch (error) {
        // Keep existing state if refresh-on-focus fails.
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [userId]);

  // Combined form data state
  const [formData, setFormData] = useState({
    media_type: '',

    title: '',
    category: '',
    hashtags: '',
    description: '',

    // Format and monetization
    format: '',
    price: '',
    edition: 'unlimited',
    copies: null,
    credits: ''
  });

  // Enforce: multiple uploads are only allowed for Digital Music (HYK-914).
  // If user switches away from Digital Music, keep only the first selected media.
  useEffect(() => {
    const isMusicDigital = formData.category === 'music' && formData.format === 'digital';
    if (isMusicDigital) return;
    if (mediaFiles.length <= 1) return;

    setMediaFiles((prev) => {
      if (!Array.isArray(prev) || prev.length <= 1) return prev;
      // Revoke previews for removed items to avoid leaking blob URLs.
      prev.slice(1).forEach((item) => {
        if (item?.preview) URL.revokeObjectURL(item.preview);
      });
      const keep = prev[0];
      if (keep?.file) {
        setMediaFile(keep.file);
        setMediaPreview(keep.preview || '');
      }
      return [keep];
    });
  }, [formData.category, formData.format, mediaFiles.length]);
  const draftPrice = Number(String(formData?.price ?? '').replace(',', '.'));
  const isPaidCreationDraft = free !== true && Number.isFinite(draftPrice) && draftPrice > 0;
  const isPublishBlockedByKyc =
    isPaidCreationDraft &&
    !kycStatusLoading &&
    (kycStatus?.has_light_kyc === false || !!kycStatusError);
  useAutoGrowTextarea(textareaRef, formData.description);
  useAutoGrowTextarea(creditsTextareaRef, formData.credits, { minLines: 3, maxLines: 10 });

  const handleAddSizeEntry = () => {
    setSizeEntries(prev => [...prev, createEmptySizeEntry()]);
    setSizeErrors([]);
  };

  const handleRemoveSizeEntry = (index) => {
    setSizeEntries(prev => prev.filter((_, i) => i !== index));
    setSizeErrors([]);
  };

  const handleSizeEntryChange = (index, field, value) => {
    setSizeEntries(prev => prev.map((entry, i) => i === index ? {
      ...entry,
      [field]: field === 'stock_quantity' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value
    } : entry));
    setSizeErrors([]);
  };

  const handleToggleSizing = (enabled) => {
    setIsSizingAndQuantitiesEnabled(enabled);
    setSizeErrors([]);
    if (enabled && sizeEntries.length === 0) {
      setSizeEntries([createEmptySizeEntry()]);
    }
    if (!enabled) {
      setSizeEntries([createEmptySizeEntry()]);
    }
  };

  // HYK-570: Generate unique ID for file
  const generateFileId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleFileChange = async (e, type) => {
    if (type === 'media') {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const isMusicDigital = formData.category === 'music' && formData.format === 'digital';
      const maxFilesAllowed = isMusicDigital ? 40 : 1;

      const currentCount = mediaFiles.length;
      if (currentCount + files.length > maxFilesAllowed) {
        setError(prev => ({
          ...prev, media: (
            <span>
              Maximum {maxFilesAllowed} file{maxFilesAllowed !== 1 ? 's' : ''} allowed. <br />
              You have {currentCount} file{currentCount !== 1 ? 's' : ''} selected. Please select {maxFilesAllowed - currentCount} or fewer files.
            </span>
          )
        }));
        e.target.value = '';
        return;
      }

      const validFiles = [];
      const errors = [];

      const readDurationSeconds = (file) => new Promise((resolve) => {
        try {
          if (!file?.type?.startsWith('audio/') && !file?.type?.startsWith('video/')) return resolve(null);
          const url = URL.createObjectURL(file);
          const el = document.createElement(file.type.startsWith('audio/') ? 'audio' : 'video');
          el.preload = 'metadata';
          el.src = url;

          const cleanup = () => {
            try { URL.revokeObjectURL(url); } catch { }
          };

          el.onloadedmetadata = () => {
            const d = Number(el.duration);
            cleanup();
            if (!Number.isFinite(d) || d <= 0) return resolve(null);
            return resolve(Math.round(d));
          };
          el.onerror = () => {
            cleanup();
            resolve(null);
          };
        } catch (err) {
          resolve(null);
        }
      });

      files.forEach((file) => {
        // Digital+Music: allow the same supported types as the generic uploader
        if (!SUPPORTED_TYPES.includes(file.type)) {
          errors.push(`${file.name}: Unsupported file type`);
          return;
        }
        if (file.size > 1024 * 1024 * 1024) {
          errors.push(`${file.name}: File exceeds 1GB limit`);
          return;
        }
        const previewUrl = URL.createObjectURL(file);
        const defaultTitle = file.name.replace(/\.[^/.]+$/, '');
        validFiles.push({
          id: generateFileId(),
          file,
          preview: previewUrl,
          url: null,
          error: null,
          uploaded: false,
          title: defaultTitle,
          durationSeconds: null,
        });
      });

      if (errors.length > 0) {
        setError(prev => ({
          ...prev, media: (
            <span>
              {errors.map((err, i) => <React.Fragment key={i}>{err}<br /></React.Fragment>)}
            </span>
          )
        }));
      }

      if (validFiles.length > 0) {
        setMediaFiles(prev => [...prev, ...validFiles]);
        setError(prev => ({ ...prev, media: null }));
        if (validFiles.length === 1 && mediaFiles.length === 0) {
          setMediaFile(validFiles[0].file);
          setMediaPreview(validFiles[0].preview);
        }

        validFiles.forEach(async (entry) => {
          const durationSeconds = await readDurationSeconds(entry.file);
          if (durationSeconds == null) return;
          setMediaFiles((prev) => prev.map((item) => item.id === entry.id ? { ...item, durationSeconds } : item));
        });
      }

      e.target.value = '';
    } else if (type === 'thumbnail') {
      const file = e.target.files[0];
      if (!file) return;
      
      const previewUrl = URL.createObjectURL(file);
      setPreviewFile(file);
      setPreviewImage(previewUrl);
      setThumbnailError(false);
    }
  };

  // HYK-570: Remove a file from selection
  const handleRemoveFile = (fileId) => {
    setMediaFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      // Clean up blob URL
      const removed = prev.find(f => f.id === fileId);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      
      // Update backward compatibility state
      if (updated.length === 0) {
        setMediaFile(null);
        setMediaPreview('');
        setFormData((prev) => ({ ...prev, tag: '' }));
      } else if (updated.length === 1) {
        setMediaFile(updated[0].file);
        setMediaPreview(updated[0].preview);
      }
      
      return updated;
    });
  };

  // HYK-570: Reorder files
  const handleReorderFiles = (fromIndex, toIndex) => {
    setMediaFiles(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const handleTrackPointerDown = (trackId) => (event) => {
    if (!event.isPrimary) return;
    draggingTrackIdRef.current = trackId;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch (e) {
      // ignore
    }
  };

  const handleTrackPointerUp = () => {
    draggingTrackIdRef.current = null;
    if (rafReorderRef.current) {
      cancelAnimationFrame(rafReorderRef.current);
      rafReorderRef.current = null;
    }
  };

  const scheduleReorderFromPointer = (clientX, clientY) => {
    if (rafReorderRef.current) return;
    rafReorderRef.current = requestAnimationFrame(() => {
      rafReorderRef.current = null;
      const draggingId = draggingTrackIdRef.current;
      if (!draggingId) return;
      const container = tracksListRef.current;
      if (!container) return;

      const el = document.elementFromPoint(clientX, clientY);
      const rowEl = el?.closest?.('[data-track-row="true"]');
      if (!rowEl) return;
      const overId = rowEl.getAttribute('data-track-id');
      if (!overId || overId === draggingId) return;

      setMediaFiles((prev) => {
        const fromIndex = prev.findIndex((t) => t.id === draggingId);
        const toIndex = prev.findIndex((t) => t.id === overId);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
        const updated = [...prev];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved);
        return updated;
      });
    });
  };

  const handleTrackPointerMove = (event) => {
    if (!draggingTrackIdRef.current) return;
    scheduleReorderFromPointer(event.clientX, event.clientY);
  };

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  // Track titles default to filename (no inline renaming input).

  // Fee structure constants (imported from globals)
  const CENTS_MULTIPLIER = 100 // For rounding to 2 decimal places

  // Simplified combined fee multiplier
  // Formula: fullPrice = creatorPrice × (1 + HYK_FEE_PERCENT) × (1 + STRIPE_FEE_PERCENT) + STRIPE_FIXED_FEE
  // Using simplified coefficient for easier calculations
  const COMBINED_FEE_MULTIPLIER = 1.115 // Approximates (1.10 × 1.015) = 1.11665

  // Calculate creator price from full price (reverse calculation)
  const calculateCreatorPriceFromFull = (fullPrice) => {
    if (!fullPrice || fullPrice === 0) return 0
    // Reverse formula: creatorPrice = (fullPrice - STRIPE_FIXED_FEE) / COMBINED_FEE_MULTIPLIER
    const rawCreatorPrice = (fullPrice - STRIPE_FIXED_FEE) / COMBINED_FEE_MULTIPLIER
    // Round up to ensure creator gets at least this amount
    return Math.ceil(rawCreatorPrice * CENTS_MULTIPLIER) / CENTS_MULTIPLIER
  }

  // Handle creator price change
  const handleCreatorPriceChange = (e) => {
    const newCreatorPrice = e.target.value === '' ? '' : parseFloat(e.target.value)
    setFormData((prev) => ({ ...prev, price: newCreatorPrice }))

    if (newCreatorPrice !== '' && !isNaN(newCreatorPrice)) {
      const calculated = calculateFullPrice(newCreatorPrice)
      setFullPrice(parseFloat(calculated?.totalAmount) || 0)
    }
  }

  // Handle full price change
  const handleFullPriceChange = (e) => {
    const newFullPrice = e.target.value === '' ? '' : parseFloat(e.target.value)
    setFullPrice(newFullPrice)

    if (newFullPrice !== '' && !isNaN(newFullPrice)) {
      // Reverse calculate creator price from full price (already rounded up)
      const estimatedCreatorPrice = calculateCreatorPriceFromFull(newFullPrice)
      setFormData((prev) => ({ ...prev, price: estimatedCreatorPrice }))
    }
  }

  // Handle all form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const isNumericField = ['copies'].includes(name);
    let newValue = isNumericField ? parseInt(value) || "1" : (name === "price") ? parseInt(value) : value;
    let errorMsg = null;

    if (name === 'credits') {
      if (value.length > 1000) {
        errorMsg = 'Maximum allowed length is 1000 characters.';
      }
      newValue = value.slice(0, 1000);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue
    }));

    setError((prev) => ({
      ...prev,
      [name]: errorMsg
    }));
  };

  const commitTag = (raw) => {
    const hashtag = raw.trim();
    if (!hashtag || my_tags.includes(hashtag)) return;

    setMyTags((prev) => [...prev, hashtag]);
    setFormData((prev) => ({ ...prev, hashtags: "" }));
  };

  const commitTags = (rawTags) => {
    // Filter out empty tags, remove duplicates within the array, and exclude existing tags
    const trimmedTags = rawTags.map(tag => tag.trim()).filter(tag => tag);
    const uniqueNewTags = [...new Set(trimmedTags)]; // Remove duplicates within new tags
    const newTags = uniqueNewTags.filter(tag => !my_tags.includes(tag)); // Exclude existing tags

    if (newTags.length === 0) {
      // Clear input even if no new tags to add
      setFormData((prev) => ({ ...prev, hashtags: "" }));
      return;
    }

    // Commit all new tags at once
    setMyTags((prev) => [...prev, ...newTags]);
    setFormData((prev) => ({ ...prev, hashtags: "" }));
  };

  const handleHashtagChange = (e) => {
    const value = e.target.value;

    // Handle whitespace (space, tab, newline) that may appear in value (e.g., from paste operations)
    // Note: Keyboard space/Enter are handled in onKeyDown to prevent them from being added
    if (/[ \t\n]/.test(value)) {
      // Split by one or more whitespace characters (space, tab, or newline)
      const tags = value.split(/[ \t\n]+/).filter(tag => tag.trim());
      
      if (tags.length > 0) {
        // Commit all tags at once
        commitTags(tags);
        return;
      }
    }

    setFormData((prev) => ({ ...prev, hashtags: value }));
  };

  // Handle hashtag input on Enter or Space key press
  // Prevents default behavior and commits the tag immediately
  const handleHashtagKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      commitTag(formData.hashtags);
    }
  };

  const handleHashtagBlur = () => {
    commitTag(formData.hashtags);
  };

  // Handle media type selection
  const handleMediaTypeChange = (value) => {
    setError(prev => ({ ...prev, category: null }));
    const isClothing = value === 'clothing';
    if (isClothing) {
      handleToggleSizing(true);
    } else {
      handleToggleSizing(false);
    }
    setFormData(prev => ({
      ...prev,
      category: value
    }));
  };

  // Handle format/edition option selection
  const handleFormatChange = (e) => {
    const { name, value } = e;
    setError(prev => ({ ...prev, [name]: null }));
    if (name === 'edition') {
      if (value === 'unlimited' || value === 'limited') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          copies: null
        }));
      } else if (value === 'unique') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          copies: 1
        }));
      }
    } else if (name === 'format') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Clear shipping method when switching from physical to digital
      if (value === 'digital') {
        setSelectedShippingMethodId(null);
        setShippingMethodError(null);
      }
    }
  }
  // Navigation functions
  const handleNext = () => {
    if (currentStep === 1) {
      // HYK-570: Check both mediaFiles array and single mediaFile for backward compatibility
      const hasMedia = mediaFiles.length > 0 || mediaFile;
      if (hasMedia && formData.category && formData.format && formData.tag) {
        setCurrentStep(prev => prev + 1);
        setError({})
        return;
      } else {
        if (!hasMedia) {
          setError(prev => ({ ...prev, media: 'Please upload a creation' }));
        }
        if (!formData.category?.length) {
          setError(prev => ({ ...prev, category: 'Please select a category' }));
        }
        if (!formData.format?.length) {
          setError(prev => ({ ...prev, format: 'Please select a format' }));
        }
        if (!formData.tag?.length) {
          setError(prev => ({ ...prev, tag: 'Please select a cultural area' }));
        }
      }
    } else if (currentStep === 2) {
      if (formData.title?.length && my_tags?.length && formData.description?.length) {
        setCurrentStep(prev => prev + 1);
        setError({})
        return;
      } else {
        if (!formData.title?.length) {
          setError(prev => ({ ...prev, title: 'Please enter a title' }));
        }
        if (!my_tags?.length) {
          setError(prev => ({ ...prev, hashtags: 'Please enter at least one hashtag' }));
        }
        if (!formData.description?.length) {
          setError(prev => ({ ...prev, description: 'Please enter a description' }));
        }
      }
    } else if (currentStep === 3) {
      // Validate shipping method for physical creations
      if (formData.format === 'physical' && !selectedShippingMethodId) {
        setShippingMethodError('Please select a shipping method for physical creations');
        return;
      } else {
        setShippingMethodError(null);
      }

      if (typeSet && (free || formData.price) && formData.edition?.length) {
        setCurrentStep(prev => prev + 1);
        setError({})
        return;
      } else {
        if (!typeSet) {
          setError(prev => ({ ...prev, type: 'Please select a price option' }));
        }
        if (!free && !formData.price) {
          setError(prev => ({ ...prev, price: 'Please type a price for your creation or set it as Free' }));
        }
        if (!formData.edition?.length || formData.edition === 'limited' && !formData.copies?.length) {
          setError(prev => ({ ...prev, edition: 'Please type the available quantity or set the quantity to unlimited or unique' }));
        }
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(-1);
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleSaveDesc = () => {
    setOpenDesc(false);
  };
  // const getProgressWidth = () => {
  //   return `${(currentStep / 5) * 100}%`;
  // };

  if (openDesc) {
    return (
      <div className="add-creation">
        <div className="add-creation__header" data-step={currentStep}>
          <button onClick={() => { setFormData(prev => ({ ...prev, description: '' })), setOpenDesc(false) }} className="add-creation__back" aria-label="Back">
            <Icon alias="chevron-left" width={24} color="#0A0F1E" />
          </button>
          <h2 className='fs-sm2 fw-bold fg-dark'>NEW CREATION</h2>
          <button
            onClick={handleSaveDesc}
            className="btn btn-sm red-btn rounded-xl fs-xxs p-absolute px-16" style={{ right: '16px', top: '50%', transform: 'translateY(-50%)' }}
          >
            SAVE
          </button>
        </div>
        <div className="add-creation-main__step">
          <NewsEditor
            className="fs-xs fg-grey7"
            value={formData.description}
            onChange={(value) => handleInputChange({ target: { name: 'description', value } })}
            placeholder={{
              title: false,
              body: 'Add more about the creation, the shipping details and even pictures.'
            }}
            disableAutoHeading={true}
          />
        </div>
      </div>
    )
  }



  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Fragment>
            <div className="add-creation__header" data-step={currentStep}>
              <button onClick={handleBack} className="add-creation__back" aria-label="Back">
                <Icon alias="chevron-left" width={24} color="#0A0F1E" />
              </button>
              <h2 className='fs-sm2 fw-bold fg-dark '>NEW CREATION</h2>
              <button
                onClick={handleNext}
                className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
              // disabled={!mediaFile || !formData.category || !formData.format}
              >
                NEXT
              </button>
            </div>
            <div className="add-creation__step">

              <div className="add-creation__content">
                <div className="add-creation__format-options">
                  <button
                    className={`add-creation__format-option ${formData.format === 'digital' ? 'active' : ''}`}
                    onClick={() => handleFormatChange({ name: 'format', value: 'digital' })}
                  >
                    <div className="add-creation__format-icon">
                      <Icon alias={'digital_format'} width={40} color={formData.format === 'digital' ? '#FFF' : '#FF3232'} />
                    </div>
                    <span className='fs-real fw-bold'>Digital</span>
                  </button>
                  <button
                    className={`add-creation__format-option ${formData.format === 'physical' ? 'active' : ''}`}
                    onClick={() => handleFormatChange({ name: 'format', value: 'physical' })}
                  >
                    <div className="add-creation__format-icon">
                      <Icon alias={'physical_format'} width={37} color={formData.format === 'physical' ? '#FFF' : '#FF3232'} />
                    </div>
                    <span className='fs-real fw-bold'>Physical</span>
                  </button>
                </div>

                {error?.format && <p className='fs-real fw-medium fg-xtred ta-center'>{error.format}</p>}
                <AddCategory onClick={handleMediaTypeChange} selectedCategory={formData.category} />
                {error?.category && <p className='fs-real fw-medium fg-xtred ta-center'>{error.category}</p>}

                {/* Cultural areas directly under category — stays visible above multi-track list (HYK-1053) */}
                {(mediaPreview || mediaFiles.length > 0) && (
                  <div className="cultural-areas d-flex f-column aic jcc">
                    <p className='fs-xxs fw-medium fg-dark mb-12 mt-0'>CULTURAL AREAS</p>
                    <div className='d-flex aic jcc area-container w-full'>
                      {(Array.isArray(tags) ? tags : []).filter(tag => tag.is_primary).map(tag => (
                        <span
                          key={tag.tag_id}
                          className={`area tt-uppercase fs-tiny fw-extra-bold ${formData.tag === tag.tag_name ? 'fg-white bg-dark' : 'fg-dark bg-white'}`}
                          onClick={() => handleInputChange({ target: { name: 'tag', value: tag.tag_name } })}
                        >
                          {capitalizeFirstLetter(tag.tag_name)}
                        </span>
                      ))}
                    </div>
                    {(Array.isArray(tags) ? tags : []).filter(tag => tag.is_primary).length === 0 && (
                      <p className="fs-xxs fw-medium fg-grey7 mt-8 mb-0 ta-center">
                        Loading cultural areas...
                      </p>
                    )}
                    {error?.tag && <p className='fs-real fw-medium fg-xtred ta-center'>{error.tag}</p>}
                  </div>
                )}

                {/* <CustomSelect
                value={formData.category}
                onChange={handleMediaTypeChange}
                className="add-creation__select"
                name="category"
                options={[
                  { value: '', label: 'Are you sharing a music, photograph or other?' },
                  {
                    value: 'music',
                    label: (
                      <>
                        <Icon alias="music" className={'mr-8'} width={32} height={32} color="#FF3232" />
                        <span>Music</span>
                      </>
                    )
                  },
                  {
                    value: 'podcast',
                    label: (
                      <>
                        <Icon alias="podcast" className={'mr-8'} width={32} height={32} color="#FF3232" />
                        <span>Podcast</span>
                      </>
                    )
                  },
                  {
                    value: 'video',
                    label: (
                      <>
                        <Icon alias="video" className={'mr-8'} width={32} height={32} color="#FF3232" />
                        <span>Video/Movie</span>
                      </>
                    )
                  },
                  {
                    value: 'artwork',
                    label: (
                      <>
                        <Icon alias="artwork" className={'mr-8'} width={32} height={32} color="#FF3232" />
                        <span>Artwork</span>
                      </>
                    )
                  },
                  {
                    value: 'photography',
                    label: (
                      <>
                        <Icon alias="library-photography" className={'mr-8'} width={32} height={32} color="#FF3232" />
                        <span>Photography</span>
                      </>
                    )
                  },
                  {
                    value: 'clothing',
                    label: <>
                      <Icon alias="clothing" className={'mr-8'} width={32} height={32} color="#FF3232" />
                      <span>Clothing</span>
                    </>
                  },
                  {
                    value: 'book',
                    label: <>
                      <Icon alias="ebook" className={'mr-8'} width={32} height={32} color="#FF3232" />
                      <span>Book/E-Book</span>
                    </>
                  },
                  {
                    value: 'craft',
                    label: <>
                      <Icon alias="craft" className={'mr-8'} width={32} height={32} color="#FF3232" />
                      <span>Craft</span>
                    </>
                  },
                ]}
              /> */}
                {/* Non-music categories: basic single-file display (legacy behavior) */}
                {mediaFiles.length > 0 && !(formData.format === 'digital' && formData.category === 'music') && (
                  <div
                    className='add-creation__main-preview-label'
                    onClick={() => handleRemoveFile(mediaFiles[0].id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRemoveFile(mediaFiles[0].id);
                    }}
                    aria-label={`Remove ${mediaFiles[0].file?.name || 'file'}`}
                  >
                    <span className="filename-text">{mediaFiles[0].file?.name}</span>
                    <Icon alias="trash_outline_red" width={24} color="none" className='ml-auto' />
                  </div>
                )}

                {/* HYK-914: music (single + multi) aligned list */}
                {formData.format === 'digital' && formData.category === 'music' && mediaFiles.length > 0 && (
                  <div className="music-tracks-editor mb-16">
                    <p className="music-tracks-editor__label fs-xxs fw-bold fg-grey7 mb-8 mt-0">Tracks ({mediaFiles.length}/40)</p>
                    <div
                      className="music-tracks-editor__list"
                      ref={tracksListRef}
                      onPointerMove={handleTrackPointerMove}
                      onPointerUp={handleTrackPointerUp}
                      onPointerCancel={handleTrackPointerUp}
                      onPointerLeave={handleTrackPointerUp}
                    >
                      {mediaFiles.map((item, index) => (
                        <div
                          key={item.id}
                          className="music-tracks-editor__row"
                          data-track-row="true"
                          data-track-id={item.id}
                        >
                          {mediaFiles.length > 1 ? (
                            <button
                              type="button"
                              className="music-tracks-editor__drag"
                              onPointerDown={handleTrackPointerDown(item.id)}
                              aria-label="Drag to reorder"
                            >
                              <Icon alias="drag_dots" width={24} color="none" />
                            </button>
                          ) : (
                            <span className="music-tracks-editor__drag-spacer" aria-hidden="true" />
                          )}
                          <div className="music-tracks-editor__meta">
                            <div className="music-tracks-editor__name" title={item.file?.name}>
                              {`${index + 1}. ${item.file?.name || ''}`}
                            </div>
                            <div className="music-tracks-editor__sub">
                              <span className="music-tracks-editor__size">{formatBytes(item.file?.size)}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(item.id)}
                            className="music-tracks-editor__delete"
                            aria-label="Delete track"
                          >
                            <Icon alias="trash_outline_red" width={24} color="none" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Backward compatibility: Show single file if only one selected */}
                {mediaFile && mediaFiles.length === 0 && (
                  <div
                    className='add-creation__main-preview-label'
                    onClick={() => {
                      setMediaFile(null);
                      setMediaPreview('');
                      setPreviewFile(null);
                      setPreviewImage('');
                      setFormData((prev) => ({ ...prev, tag: '' }));
                    }}
                  >
                    <span className="filename-text">{mediaFile.name}</span>
                    <Icon alias="trash_outline_red" width={24} color="none" className='ml-auto' />
                  </div>
                )}

                {(() => {
                  const isMusicDigital = formData.category === 'music' && formData.format === 'digital';
                  const max = isMusicDigital ? 40 : 1;
                  // Only Digital Music can add more. Other categories: hide upload box once 1 file exists.
                  if (!isMusicDigital && (mediaFiles.length > 0 || !!mediaFile)) return false;
                  return mediaFiles.length < max;
                })() && (
                  <div className={`add-creation__upload-area ${formData.category === 'music' && formData.format === 'digital' && mediaFiles.length > 0 ? 'add-creation__upload-area--compact' : ''}`}>
                    <label className="add-creation__upload-button">
                      <input
                        type="file"
                        onChange={(e) => handleFileChange(e, 'media')}
                        accept=".png,.jpg,.jpeg,.gif,.mp4,.webm,.ogg,.mp3,.wav,.flac,.pdf"
                        multiple={formData.category === 'music' && formData.format === 'digital'
                          ? true
                          : false}
                        hidden
                      />
                      <div className="add-creation__upload-placeholder">
                        {formData.category === 'music' && formData.format === 'digital' && mediaFiles.length > 0 ? (
                          <div className="add-creation__upload-compact">
                            <span className="add-creation__upload-compact-text">Add more</span>
                            <Icon alias="plus" width={24} stroke="#0A0F1E" />
                          </div>
                        ) : (
                          <>
                            <Icon alias='plus' width={24} stroke='#0A0F1E' />
                            <span className='fs-sm2 fw-bold fg-dark'>
                              Add your creation
                            </span>
                            <p className='fs-xxs fw-bold fg-grey7'>UP TO 1GB per file</p>
                            <p className='fs-xxs fw-medium fg-grey7'>
                              Images (PNG, JPG, GIF), Videos (MP4, WebM, OGG), Audio (MP3, WAV, FLAC, OGG), Text (PDF)
                            </p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                )}
                {formData.format === 'digital' && formData.category === 'music' && mediaFiles.length === 0 && (
                  <p className="add-creation__multi-files-hint">
                    YOU CAN NOW ADD MULTIPLE FILES WHEN IT&apos;S DIGITAL MUSIC OR PODCAST
                  </p>
                )}
                {error?.media && <p className='fs-real fw-medium fg-xtred ta-center'>{error.media}</p>}

                {/* {mediaPreview && !previewImage &&
                <div className="add-creation__preview-upload">

                  <label className="add-creation__upload-button">
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, 'thumbnail')}
                      accept="image/*"
                      hidden
                    />
                    <div className="add-creation__upload-placeholder">
                      <i className="fas fa-plus"></i>
                      <span>Add a thumbnail</span>
                    </div>
                  </label>
                </div>
              } */}
              </div>
              {/* <button
              className="add-creation__next btn red-outline-btn   mt-auto"
              onClick={handleNext}
              disabled={(mediaFiles.length === 0 && !mediaFile) || !formData.category || !previewImage}
            >
              Next
            </button> */}
            </div>
          </Fragment>
        );

      case 2:
        return (
          <Fragment>
            <div className="add-creation__header" data-step={currentStep}>
              <button onClick={handleBack} className="add-creation__back" aria-label="Back">
                <Icon alias="chevron-left" width={24} color="#0A0F1E" />
              </button>
              <h2 className='fs-sm2 fw-bold fg-dark'>NEW CREATION</h2>
              <button
                onClick={handleNext}
                className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
              // disabled={!mediaFile || !formData.category || !formData.format}
              >
                NEXT
              </button>
            </div>
            <div className="add-creation__step">

              <div className="add-creation__content mt-16">
                <input
                  type="text"
                  name="title"
                  placeholder="Title..."
                  value={formData.title}
                  onChange={handleInputChange}
                  className="add-creation__input"
                />
                {error?.title && <p className='fs-real fw-medium fg-xtred ta-center'>{error.title}</p>}
                {/* <select
                name="tag"
                value={formData.tag}
                onChange={handleInputChange}
                className="add-creation__select"
              >
                <option value="">What's your creation about?</option>
                {tags.filter(tag => tag.is_primary).map(tag =>
                  <option key={tag.tag_id} value={tag.tag_name}>{capitalizeFirstLetter(tag.tag_name)}</option>
                )}

              </select> */}

                <input
                  type="text"
                  name="hashtags"
                  placeholder="Hashtags..."
                  value={formData.hashtags}
                  onChange={handleHashtagChange}
                  onKeyDown={handleHashtagKeyPress}
                  onBlur={handleHashtagBlur}
                  className="add-creation__input"
                />
                {error?.hashtags && <p className='fs-real fw-medium fg-xtred ta-center'>{error.hashtags}</p>}
                <div className="hashtags-container">
                  {my_tags.map(tag => (
                    <span key={tag} className="hashtag fs-tiny fw-extra-bold fg-tag-blue d-flex aic">#{tag} <Icon alias="cross_v2" className="ml-8" width={20} stroke="#4EB9E8" onClick={() => setMyTags(prev => prev.filter(t => t !== tag))} /></span>
                  ))}
                </div>

                <div className="p-relative">
                  <div

                    // ref={textareaRef}
                    rows={1}
                    name="description"
                    placeholder="About..."
                    value={formData.description}
                    onClick={() => setOpenDesc(true)}
                    onChange={() => { }}
                    className="add-creation__input "
                    style={{ height: '48px' }}
                  >
                    <span className={`fw-medium ${formData.description?.length ? 'fg-dark' : 'fg-grey7'} `} style={{ maxWidth: '280px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: "inline-block" }}>
                      {formData.description?.length ? getFirstParagraphText(formData.description) || getFirstTitleFromHtml(formData.description) : 'About...'}
                    </span>
                  </div>
                  <Icon alias="chevron-right" width={24} color="#0A0F1E" className="chevright-icon" />

                </div>
              </div>
              {error?.description && <p className='fs-real fw-medium fg-xtred ta-center'>{error.description}</p>}

              {/* Credits & Copyright Section */}
              <div className="add-creation__credits-section mt-16">
                <div className="add-creation__copies">
                  <textarea
                    ref={creditsTextareaRef}
                    name="credits"
                    placeholder="Credits & Copyright..."
                    value={formData.credits}
                    onChange={handleInputChange}
                    className="add-creation__input"
                    rows="3"
                    style={{ resize: 'none', overflow: 'hidden' }}
                  />
                  <span className="add-creation__copies-label">{1000 - (formData.credits?.length || 0)}</span>
                </div>
              </div>
              {error?.credits && <p className='fs-real fw-medium fg-xtred ta-center'>{error.credits}</p>}

              {/* <button
              className="add-creation__next btn red-outline-btn   mt-auto "
              onClick={handleNext}
              disabled={!formData.title || !formData.tag || !formData.description || !checkCommaCount(formData.hashtags)}
            >
              Next
            </button> */}
            </div>
          </Fragment>
        );

      case 3:
        return (
          <Fragment>
            <div className="add-creation__header" data-step={currentStep}>
              <button onClick={handleBack} className="add-creation__back" aria-label="Back">
                <Icon alias="chevron-left" width={24} color="#0A0F1E" />
              </button>
              <h2 className='fs-sm2 fw-bold fg-dark'>NEW CREATION</h2>
              <button
                onClick={handleNext}
                className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
              // disabled={!mediaFile || !formData.category || !formData.format}
              >
                NEXT
              </button>
            </div>
            <div className="add-creation__step">

              <div className="add-creation__content">

                <div className="cultural-areas d-flex f-column aic jcc">
                  <p className='fs-xxs fw-medium fg-dark mb-12 mt-0'>PRICE</p>
                  <div className="d-flex aic jcc area-container w-full">
                    <span
                      className={`area price fs-real fw-bold ${typeSet ? (free ? 'bg-dark fg-white' : 'bg-white fg-dark') : 'fg-dark bg-white'}`}
                      onClick={() => {
                        setFree(true);
                        setTypeSet(true);
                        setFormData((prev) => ({ ...prev, price: '' }));
                        setFullPrice('');
                        setError(prev => ({ ...prev, price: null }));
                      }}
                    >
                      Free
                    </span>
                    <span
                      className={`area price fs-real fw-bold ${typeSet ? (free ? 'bg-white fg-dark' : 'bg-dark fg-white') : 'fg-dark bg-white'}`}
                      onClick={() => {
                        setFree(false);
                        setTypeSet(true);
                        setError(prev => ({ ...prev, price: null }));
                      }}
                    >
                      Fixed Price
                    </span>
                  </div>

                </div>


                {!free && typeSet &&
                  <><div className="add-creation__price-input">
                    <input
                      type="number"
                      name="creatorPrice"
                      placeholder="Creation price"
                      value={formData.price}
                      onChange={handleCreatorPriceChange}
                      className="add-creation__input"
                      step="0.01"
                      min="0"
                    />
                    <span className="add-creation__currency">€</span>

                  </div>
                    {error?.price && <p className='fs-real fw-medium fg-xtred ta-center'>{error.price}</p>}

                    <div className="add-creation__price-input">
                      <input
                        type="number"
                        name="fullPrice"
                        placeholder="Full price with fees"
                        value={fullPrice}
                        onChange={handleFullPriceChange}
                        className="add-creation__input fg-pink fw-bold"
                        step="0.01"
                        min="0"
                      />
                      <span className="add-creation__currency fg-pink">€</span>

                    </div>
                    <Fragment>
                      <span
                        className="fg-pink fw-medium d-flex aic fs-regular fw-bold price-info-clickable"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowPriceDetails(true)}
                        aria-label="View detailed price breakdown"
                      >price details
                        <Icon alias={"info_v2"} color="#FF8CDC" width={16} className="ml-4" />
                      </span>
                    </Fragment></>}
                {showPriceDetails && <PriceDetailsAddCreation onClose={() => setShowPriceDetails(false)} price={formData.price} />}

                {/* Download Toggle - Only for paid digital creations with files */}
                {formData.format === 'digital' && mediaFile && !free && (
                  <div className='d-flex f-column jcc mt-16'>
                    <div className='d-flex f-row aic jcc' style={{ marginBottom: '8px' }}>
                      <p className='fg-dark fw-medium fs-xxs tt-uppercase'>Download</p>
                    </div>
                    <div className='d-flex f-row aic jcsb'>
                      <p className='fs-regular fw-medium fg-dark'>Allow to download files</p>
                      <Toggle enabled={allowDownload} setEnabled={setAllowDownload} />
                    </div>
                  </div>
                )}

                <div className="cultural-areas d-flex f-column aic jcc mt-16">
                  {false ? <p className='fs-xxs fw-medium fg-dark mb-12 mt-0'>QUANTITY</p> :
                    <div className='d-flex aic jcc f-row'>
                      <p className='fg-dark fw-medium fs-xxs tt-uppercase'>Size & Quantity</p>
                      <Icon alias="size_quantity" width={16} color="#FF3232" />
                    </div>}

                  <div className="d-flex aic jcc area-container w-full">
                    <span
                      className={`area price fs-real fw-bold ${formData.edition === 'unlimited' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`}
                      onClick={() => handleFormatChange({ name: 'edition', value: 'unlimited' })}
                    >Unlimited</span>
                    <span
                      className={`area price fs-real fw-bold ${formData.edition === 'limited' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`}
                      onClick={() => handleFormatChange({ name: 'edition', value: 'limited' })}>Limited</span>
                    <span
                      className={`area price fs-real fw-bold ${formData.edition === 'unique' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`}
                      onClick={() => handleFormatChange({ name: 'edition', value: 'unique' })}>Unique</span>
                  </div>

                </div>

                {/* Shipping Methods Section - Only for Physical Creations */}
                {formData.format === 'physical' && (
                  <div className="cultural-areas d-flex f-column aic jcc mt-16">
                    <div className='d-flex aic jcc f-row'>
                      <p className='fg-dark fw-medium fs-xxs tt-uppercase'>Shipping Methods</p>
                    </div>
                    <div className="d-flex f-column w-full mt-8">
                      <div
                        className="shipping-method-selector-input"
                        onClick={() => {
                          setShowShippingMethodSelector(true);
                          setShippingMethodError(null); // Clear error when opening selector
                        }}
                        style={{
                          width: '100%',
                          height: '40px',
                          background: 'rgba(249, 249, 249, 0.64)',
                          border: shippingMethodError ? '1px solid #FF3232' : '1px solid #E5E5E5',
                          borderRadius: '20px',
                          padding: '0 16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer'
                        }}
                      >
                        <span style={{
                          color: selectedShippingMethodId ? '#1C1B1F' : '#999',
                          fontSize: '16px',
                          fontWeight: selectedShippingMethodId ? 500 : 400
                        }}>
                          {(() => {
                            if (!selectedShippingMethodId) {
                              return 'Select shipping method';
                            }
                            const selectedMethod = shippingMethods.find(
                              m => (m.shipping_method_id || m.id) === selectedShippingMethodId
                            );
                            if (!selectedMethod) {
                              return 'Shipping method selected';
                            }
                            // Format the display text
                            const title = selectedMethod.title || (selectedMethod.type === 'general' ? 'General' : 'Specific');
                            const destinations = selectedMethod.destinations || [];
                            const destinationText = destinations.length > 0
                              ? (destinations.length <= 2
                                ? destinations.map(d => d.country_name || d.name || d).join(', ')
                                : `${destinations.slice(0, 2).map(d => d.country_name || d.name || d).join(', ')} + ${destinations.length - 2} more`)
                              : '';
                            const rates = selectedMethod.rates || [];
                            const rateText = rates.length > 0
                              ? ` - €${parseFloat(rates[0]?.rate || 0).toFixed(2)}`
                              : '';
                            return `${title}${destinationText ? ` (${destinationText})` : ''}${rateText}`;
                          })()}
                        </span>
                        <Icon alias="chevron-down" width={16} color="#1C1B1F" />
                      </div>
                      {shippingMethodError && (
                        <p className="fs-xs fg-red-pink fw-medium mt-4 mb-0" style={{ width: '100%', textAlign: 'left' }}>
                          {shippingMethodError}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* <div className="add-creation__edition-options">
                  <button
                    className={`add-creation__edition-option ${formData.edition === 'limited' ? 'active' : ''}`}
                    onClick={() => handleFormatChange({ name: 'edition', value: 'limited' })}
                  >
                    Limited Edition
                  </button>
                  <button
                    className={`add-creation__edition-option ${formData.edition === 'unlimited' ? 'active' : ''}`}
                    onClick={() => handleFormatChange({ name: 'edition', value: 'unlimited' })}
                  >
                    Unlimited Edition
                  </button>
                </div> */}
                {formData.category === 'clothing' && formData.edition !== 'unique' && (
                  <div className='d-flex f-column jcc '>
                    <div className='d-flex f-row aic jcsb'>
                      <p className='fs-regular fw-medium fg-dark'>Sizing and Quantities</p>

                      <Toggle enabled={isSizingAndQuantitiesEnabled} setEnabled={handleToggleSizing} />
                    </div>
                    {isSizingAndQuantitiesEnabled && (
                      <div className="add-creation__copies">
                        {sizeEntries.map((entry, index) => (
                          <div
                            key={index}
                            className='d-flex f-row aic'
                            style={{ gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}
                          >
                            <input
                              type="text"
                              name={`size_label_${index}`}
                              placeholder="Size label (S, M, L...)"
                              value={entry.size_label}
                              onChange={(e) => handleSizeEntryChange(index, 'size_label', e.target.value)}
                              className="add-creation__input"
                              style={{ flex: '1 1 180px' }}

                            />
                            {formData.edition === 'limited' && (
                              <input
                                type="number"
                                name={`stock_quantity_${index}`}
                                placeholder="Stock (optional)"
                                value={entry.stock_quantity}
                                onChange={(e) => handleSizeEntryChange(index, 'stock_quantity', e.target.value)}
                                className="add-creation__input"
                                min="0"
                                step="1"
                                style={{ flex: '0 0 160px' }}
                              />
                            )}
                            {sizeEntries.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveSizeEntry(index)}
                                className='fs-xxs fw-bold fg-red'
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <div className='d-flex f-row aic'><button
                          type="button"
                          onClick={handleAddSizeEntry}
                          className='fg-red fs-xxs fw-extra-bold tt-uppercase td-underline td-solid'
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', alignSelf: 'flex-start' }}
                        >
                          Add Size
                        </button>
                          <Icon alias="plus" width={12} color="#FF3232" style={{ marginLeft: '-8px' }} />
                        </div>

                        {sizeErrors.length > 0 && (
                          <ul className='mt-8' style={{ paddingLeft: '16px', color: '#FF3232', fontSize: '12px', fontWeight: 600 }}>
                            {sizeErrors.map((msg, idx) => (
                              <li key={idx}>{msg}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {formData.edition === 'limited' && formData.category !== 'clothing' && (
                  <div className="add-creation__copies">
                    <input
                      type="number"
                      name="copies"
                      placeholder="Quantity"
                      value={formData.copies}
                      onChange={handleInputChange}
                      className="add-creation__input"
                    />
                    <span className="add-creation__copies-label">Copies</span>
                  </div>
                )}
                {error?.edition && <p className='fs-real fw-medium fg-xtred ta-center'>{error.edition}</p>}

                {/* Shipping Method Selector Modal - Only for Physical Creations */}
                {formData.format === 'physical' && showShippingMethodSelector && (
                  <ShippingMethodSelector
                    selectedMethodId={selectedShippingMethodId}
                    onSelect={(methodId) => {
                      setSelectedShippingMethodId(methodId);
                      setShippingMethodError(null); // Clear error when method is selected
                    }}
                    onClose={() => setShowShippingMethodSelector(false)}
                  />
                )}

              </div>
              {/* <button
                className="add-creation__next btn red-outline-btn   mt-auto"
                onClick={handleNext}
                disabled={!(formData.format && formData.format.length > 0) || (formData.edition === 'limited' && !formData.copies)}
              >
                Next
              </button> */}
            </div>
          </Fragment>
        );
      case 4:
        return (
          <Fragment>
            <div className="add-creation__header" data-step={currentStep}>
              <button onClick={handleBack} className="add-creation__back" aria-label="Back">
                <Icon alias="chevron-left" width={24} color="#0A0F1E" />
              </button>
              <h2 className='fs-sm2 fw-bold fg-dark'>NEW CREATION</h2>
            </div>
            <div className="add-creation__step">
              <div className="add-creation__content">
                <CreationCard
                  {...formData}
                  sizes={formData.category === 'clothing' && isSizingAndQuantitiesEnabled ? sizeEntries : []}
                  tags={[
                    ...my_tags.reduce((acc, tag) => ([...acc, { name: tag, is_primary: false }]), []),
                    { name: formData.tag, is_primary: true }]
                  }
                  upload_thumbnail={true}
                  thumbnailUrl={previewImage}
                  thumbnailFile={previewFile}
                  onFileChange={handleFileChange}
                  onDeleteThumbnail={() => {
                    setPreviewFile(null);
                    setPreviewImage('');
                    setThumbnailError(false);
                  }}
                  users={user}
                />
                {thumbnailError && !previewFile && (
                  <p className='fs-real fw-medium fg-xtred ta-center mt-8'>Please upload a thumbnail for your creation</p>
                )}
              </div>
              <div className="px-8 d-flex f-column aic jcfe mt-48">
                {isPublishBlockedByKyc && (
                  <div
                    className="w-full mb-12 p-12"
                    style={{ border: '1px solid #FFB020', borderRadius: '12px', background: '#FFF8E5' }}
                    role="status"
                    aria-live="polite"
                  >
                    <p className="fs-xs fw-medium fg-dark m-0">
                      Account verification is required before publishing.
                    </p>
                    <button
                      type="button"
                      onClick={handleStartLightVerification}
                      className="fs-xs fw-bold td-underline mt-8"
                      style={{ border: 'none', background: 'transparent', color: '#C9100E', padding: 0, cursor: 'pointer' }}
                      aria-label="Start account verification"
                      disabled={verificationLoading}
                    >
                      {verificationLoading ? 'Starting verification...' : 'Complete verification'}
                    </button>
                    {connectInstance ? (
                      <div className="mt-12">
                        <StripeEmbeddedOnboarding
                          connectInstance={connectInstance}
                          onExit={handleEmbeddedOnboardingExit}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
                {kycStatusError && (
                  <p className="fs-tiny fw-medium fg-xtred ta-center mb-8">{kycStatusError}</p>
                )}
                <button className='btn red-btn btn-full rounded-xxl mb-24 fs-real fw-extra-bold' onClick={handleSubmit} disabled={loading || (kycStatusLoading && isPaidCreationDraft) || isPublishBlockedByKyc}>{loading ? "PUBLISHING..." : "PUBLISH"}</button>
                <span onClick={() => !loading && handleClose()} className='fs-xxs fw-bold fg-dark td-underline d-flex jcc aic mb-24' disabled={loading}>CANCEL</span>
              </div>
            </div>
          </Fragment>
        )

      // case 5:
      //   return (
      //     <div className="add-creation__step p-0">
      //       <div className="add-creation__header m-16" data-step={currentStep}>
      //         <button onClick={handleBack} className="add-creation__back" aria-label="Back">
      //           <Icon alias="chevron-left" width={24} color="#0A0F1E" />
      //         </button>
      //         <h2 className='fs-sm2 fw-medium-bold fg-crea' >Preview mode</h2>
      //         <button onClick={handleClose} className="add-creation__close" aria-label="Close">
      //           <i className="fas fa-times"></i>
      //         </button>
      //       </div>
      //       <ViewCreation
      //         preview={true}
      //         creation={{
      //           ...formData,
      //           tags: formData.hashtags
      //             ? formData.hashtags.split(/[,;\s]+/).map(tag => {
      //               const trimmedTag = tag.trim();
      //               return trimmedTag.startsWith('#') ? trimmedTag.substring(1) : trimmedTag;
      //             }).filter(Boolean)
      //             : [],
      //           media: {
      //             media_url: mediaPreview,
      //             media_type: mediaFile?.type.startsWith('video/') ? 'video' :
      //               mediaFile?.type.startsWith('audio/') ? 'audio' :
      //                 mediaFile?.type.startsWith('image/') ? 'image' :
      //                   mediaFile?.type.startsWith('application/pdf') ? 'pdf' : 'other',
      //             max_stock: formData.edition === 'limited' ? formData.copies : null,
      //             current_stock: formData.edition === 'limited' ? formData.copies : null,
      //           },
      //           users: user
      //         }}
      //         handleSubmit={handleSubmit}
      //         handleBackFromPreview={() => setCurrentStep(prev => prev - 1)}
      //       />
      //     </div>
      //   );

      default:
        return null;
    }
  };
  const handleEmbeddedOnboardingExit = async () => {
    try {
      const status = await exitEmbeddedOnboarding();
      setKycStatus(deriveAddCreationKycStatus(status));
      setKycStatusError(null);
    } catch (error) {
      console.error('Failed to refresh KYC after onboarding:', error);
      setKycStatusError('Unable to refresh verification status right now.');
    }
  };

  const handleStartLightVerification = async () => {
    setVerificationLoading(true);
    setKycStatusError(null);
    try {
      let accountId = kycStatus?.account_id;
      if (!accountId) {
        const latestStatusResponse = await getKycStatus();
        if (latestStatusResponse?.success && latestStatusResponse?.data) {
          const latestStatus = deriveAddCreationKycStatus(latestStatusResponse.data);
          setKycStatus(latestStatus);

          if (latestStatus.has_light_kyc) {
            return;
          }
          accountId = latestStatus.account_id;
        }
      }

      if (!accountId) {
        // The Stripe account country is immutable — only create an account
        // with an explicit country taken from the creator's main address.
        const onboardingPayload = await buildOnboardingPayloadFromMainAddress();
        if (!onboardingPayload?.country) {
          setKycStatusError(
            'Add your address (including your country) in your profile before verification.'
          );
          return;
        }
        const onboardingResponse = await completeOnboarding(onboardingPayload);
        if (!onboardingResponse?.success || !onboardingResponse?.data?.account_id) {
          setKycStatusError(
            onboardingResponse?.message ||
            'Unable to start verification right now.'
          );
          return;
        }
        accountId = onboardingResponse.data.account_id;
      }
      await initializeEmbeddedOnboarding(accountId);
    } catch (error) {
      console.error('Failed to start light KYC onboarding:', error);
      setKycStatusError(
        error?.response?.message ||
        error?.message ||
        'Unable to start verification right now.'
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  // Submit function
  const handleSubmit = async () => {
    if (isPublishBlockedByKyc) {
      alert('Please complete account verification before publishing your creation.');
      return;
    }

    const hasMediaFiles = mediaFiles.length > 0 || mediaFile;
    if (!userId || !hasMediaFiles || !formData.title || !formData.category || !formData.description) {
      alert('Please fill in all required fields: title, category, description, and media');
      return;
    }

    // HYK-539: Make thumbnail mandatory
    if (!previewFile) {
      setThumbnailError(true);
      return;
    }

    // Validate shipping method for physical creations
    if (formData.format === 'physical' && !selectedShippingMethodId) {
      setShippingMethodError('Please select a shipping method for physical creations');
      return;
    } else {
      setShippingMethodError(null);
    }

    let sizesPayload = [];
    if (formData.category === 'clothing') {
      if (isSizingAndQuantitiesEnabled) {
        const payload = [];
        const errors = [];

        sizeEntries.forEach((entry, index) => {
          const label = entry.size_label?.trim();
          const stockInput = entry.stock_quantity;
          const rowNumber = index + 1;

          const isCompletelyEmpty = !label && (stockInput === '' || stockInput === null || stockInput === undefined);
          if (isCompletelyEmpty) {
            return;
          }

          if (!label) {
            errors.push(`Row ${rowNumber}: enter a size label.`);
            return;
          }

          if (stockInput === '' || stockInput === null || stockInput === undefined) {
            payload.push({ size_label: label });
            return;
          }

          const parsedStock = Number(stockInput);
          if (!Number.isInteger(parsedStock) || parsedStock < 0) {
            errors.push(`Row ${rowNumber}: stock must be a non-negative integer.`);
            return;
          }

          payload.push({ size_label: label, stock_quantity: parsedStock });
        });

        if (payload.length === 0) {
          sizeEntries.length === 0
            ? errors.push('Add at least one size to continue.')
            : errors.push('Add at least one size with a label.');
        }

        if (errors.length) {
          setSizeErrors(errors);
          return;
        }

        setSizeErrors([]);
        sizesPayload = payload;
      } else {
        sizesPayload = [];
        setSizeErrors([]);
      }
    } else {
      setSizeErrors([]);
      sizesPayload = [];
    }

    try {
      setLoading(true)
      dispatch(uploadRequest())
      dispatch(updateUploadProgress(5)) // Show initial progress
      navigate('/explore');

      // Calculate final price (full price with fees) to save
      const finalPrice = fullPrice && typeof fullPrice === 'number'
        ? parseFloat(fullPrice.toFixed(2))
        : (formData.price ? parseFloat(calculateFullPrice(formData.price)?.totalAmount).toFixed(2) : '0')

      // Helper function to safely convert to number
      const safeParseFloat = (value) => {
        if (!value || value === '' || isNaN(value)) return 0;
        return parseFloat(value);
      };

      const safeParseInt = (value) => {
        if (value === '' || value === null || value === undefined || isNaN(value)) return 0;
        return parseInt(value, 10);
      };



      const fields = {
        title: formData.title,
        category: formData.category,
        tags: my_tags,
        ...formData.tag && { tag: formData.tag },
        description: formData.description,
        format: formData.format,
        price: safeParseFloat(formData.price),
        edition_type: formData.edition,
        credits: (formData.credits),
        ...(formData.edition === 'limited' && formData.copies) && { copies: safeParseInt(formData.copies) },
        ...(formData.edition === 'unique') && { max_stock: 1 },
        ...(formData.category === 'clothing' ? { sizes: sizesPayload } : { sizes: [] }),
        // Only include shipping_method_id for physical creations
        ...(formData.format === 'physical' && selectedShippingMethodId && { shipping_method_id: selectedShippingMethodId }),
        // Include allow_download for digital creations (sent in media object on backend)
        ...(formData.format === 'digital' && { allow_download: allowDownload })
      }
      try {
        dispatch(updateUploadProgress(10)); // Starting media upload

        // Determine items to upload (keep a stable mapping between UI track rows and uploaded URLs)
        const mediaItemsToUpload = mediaFiles.length > 0
          ? mediaFiles.map((item, idx) => ({ source: 'mediaFiles', idx, item, file: item.file }))
          : mediaFile
            ? [{ source: 'mediaFile', idx: 0, item: null, file: mediaFile }]
            : [];

        if (mediaItemsToUpload.length === 0) {
          throw new Error('No media files to upload');
        }

        const firstUploadFile = mediaItemsToUpload[0]?.file;
        if (mediaItemsToUpload.length === 1 && firstUploadFile?.type === 'application/pdf') {
          fields.file_type = 'pdf';
          fields.file_size = firstUploadFile.size;
          fields.file_name = firstUploadFile.name;
          fields.width = null;
          fields.height = null;
          fields.duration = null;
        }

        const uploaded = [];
        const totalFiles = mediaItemsToUpload.length;
        let uploadedCount = 0;
        const uploadErrors = [];

        // Upload files with overall progress tracking (not per-file)
        for (let i = 0; i < mediaItemsToUpload.length; i++) {
          const entry = mediaItemsToUpload[i];
          const file = entry.file;
          const fileExt = file.name.split('.').pop();
          const fileName = `${userId}_${Date.now()}_${i}.${fileExt}`;
          const filePath = `creations-media/${userId}/${fileName}`;

          try {
            // Use TUS resumable upload for large files (> 50MB)
            const useTUS = file.size > 50 * 1024 * 1024; // 50MB threshold

            if (useTUS) {
              console.log(`Using TUS upload for large file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

              const { data: { session: initialSession } } = await supabase.auth.getSession();
              if (!initialSession?.access_token) {
                throw new Error('You must be logged in to upload files');
              }

              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              const getAccessToken = async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.access_token) {
                  throw new Error('Session expired. Please refresh and try again.');
                }
                return session.access_token;
              };

              const publicUrl = await new Promise((resolve, reject) => {
                uploadFileWithTUS(file, 'creations-files', filePath, {
                  projectUrl: supabaseUrl,
                  getAccessToken,
                  onProgress: (percent) => {
                    // Calculate overall progress: (uploaded files + current file progress) / total files
                    // Map to 10-60% range for media uploads
                    const fileProgress = percent / 100;
                    const overallProgress = 10 + ((uploadedCount + fileProgress) / totalFiles) * 50;
                    dispatch(updateUploadProgress(Math.round(overallProgress)));
                  },
                  onSuccess: (url) => resolve(url),
                  onError: (error) => reject(error)
                });
              });

              uploaded.push({ ...entry, url: publicUrl });
            } else {
              // Standard upload for smaller files
              console.log(`Using standard upload for file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

              const uploadResult = await uploadFileStandard(
                supabase,
                'creations-files',
                filePath,
                file
              );

              if (uploadResult.error) {
                throw uploadResult.error;
              }

              const publicUrl = supabase.storage
                .from('creations-files')
                .getPublicUrl(filePath).data.publicUrl;

              uploaded.push({ ...entry, url: publicUrl });
            }

            uploadedCount++;
            // Update overall progress after each file completes
            const overallProgress = 10 + ((uploadedCount / totalFiles) * 50);
            dispatch(updateUploadProgress(Math.round(overallProgress)));

          } catch (error) {
            console.error(`Upload error for file ${file.name}:`, error);
            uploadErrors.push({ fileName: file.name, error: error.message, index: i });
            // Continue with other files even if one fails
          }
        }

        // If all files failed, throw error
        if (uploaded.length === 0) {
          throw new Error(`All file uploads failed: ${uploadErrors.map(e => e.fileName).join(', ')}`);
        }

        // If some files failed, show warning but continue
        if (uploadErrors.length > 0) {
          console.warn('Some files failed to upload:', uploadErrors);
          alert(`Warning: ${uploadErrors.length} file(s) failed to upload. The creation will be published with ${uploaded.length} file(s).`);
        }

        // HYK-570: Send array of media URLs instead of single media_url
        if (uploaded.length > 0) {
          const isMusicDigital = fields.category === 'music' && fields.format === 'digital';
          // Album mode only if multiple track rows were selected (not if we're falling back to legacy mediaFile)
          const isAlbumMode = isMusicDigital && mediaFiles.length > 1;

          if (isAlbumMode) {
            // HYK-914: send ordered track payload for music multi-track.
            // IMPORTANT: Only include successfully-uploaded tracks, in their original UI order.
            const uploadedTracks = uploaded
              .filter((u) => u.source === 'mediaFiles')
              .sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0))
              .map((u, idx) => ({
                media_url: u.url,
                sort_order: idx,
                title: u.item?.title || `Track ${idx + 1}`,
                duration_seconds: Number.isFinite(u.item?.durationSeconds) ? Math.round(u.item.durationSeconds) : null,
                mime_type: u.file?.type || null,
                file_size: u.file?.size ?? null,
              }));

            if (uploadedTracks.length === 0) {
              throw new Error('No music tracks were successfully uploaded.');
            }

            fields.media_items = uploadedTracks;
            // Keep media_url for backward compatibility where some downstream relies on it
            fields.media_url = uploadedTracks[0].media_url;
          } else {
            const urls = uploaded.map((u) => u.url);
            fields.media_urls = urls;
            // Keep media_url for backward compatibility if only one file
            if (urls.length === 1) {
              fields.media_url = urls[0];
            }
          }
          console.log(`Uploaded ${uploaded.length} media file(s):`, uploaded.map((u) => u.url));
        } else {
          console.error('No media URLs to send - all uploads may have failed');
          throw new Error('No media files were successfully uploaded');
        }

        dispatch(updateUploadProgress(65)); // All media uploads complete

      } catch (error) {
        console.error('Upload error:', error);
        dispatch(uploadFailure());
        alert(error.message || 'Upload failed. Please check your connection and try again.');
        setLoading(false);
        return;
      }

      // store preview image (thumbnails are small, use standard upload)
      if (previewFile) {
        try {
          dispatch(updateUploadProgress(70)); // Starting thumbnail upload

          const thumbnailFileExt = previewFile.name.split('.').pop();
          const thumbnailFileName = `${userId}_${Date.now()}.${thumbnailFileExt}`;
          const thumbnailFilePath = `creations-thumbnail/${userId}/${thumbnailFileName}`;

          const thumbnailResult = await uploadFileStandard(
            supabase,
            'creations-files',
            thumbnailFilePath,
            previewFile
          );

          if (thumbnailResult.error) {
            console.error('Thumbnail upload error:', thumbnailResult.error);
            dispatch(uploadFailure());
            alert(`Thumbnail upload failed: ${thumbnailResult.error.message}. Please try again.`);
            setLoading(false);
            return;
          }

          fields.thumbnail_url = supabase.storage
            .from('creations-files')
            .getPublicUrl(thumbnailFilePath).data.publicUrl;

          dispatch(updateUploadProgress(85)); // Thumbnail uploaded

        } catch (error) {
          console.error('Thumbnail upload error:', error);
          dispatch(uploadFailure());
          alert(error.message || 'Thumbnail upload failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      dispatch(updateUploadProgress(90)); // Saving creation to database

      // HYK-570: Log what we're sending to verify media_urls
      console.log('📤 Sending creation data to backend:', {
        title: fields.title,
        category: fields.category,
        media_urls: fields.media_urls,
        media_url: fields.media_url,
        media_urls_count: fields.media_urls?.length || 0,
        has_thumbnail: !!fields.thumbnail_url,
        format: fields.format,
        price: fields.price
      });

      postCreation(fields).then(({ success, data, message }) => {
        cachingService.invalidate([`/api/creations/${userId}`], true);
        setLoading(false)

        if (success) {
          dispatch(updateUploadProgress(100)); // Complete!
          dispatch(uploadSuccess())
          dispatch({
            type: 'ADD_CREATION_SUCCESS',
            payload: data
          });
          // Show social networks prompt if creator has no social links
          if (socialNetworksPrompt.shouldShow) {
            socialNetworksPrompt.openModal();
          }
        } else {
          console.error('Creation save failed:', { success, data, message });
          dispatch(uploadFailure());
          alert(message || 'Failed to save creation. Please try again.');
        }
      }).catch((error) => {
        console.error('Error saving creation:', error);
        dispatch(uploadFailure());
        alert(error.message || 'An error occurred while saving your creation. Please try again.');
        setLoading(false);
      });
    } catch (error) {
      console.error('Error publishing creation:', error);
      alert('An error occurred while publishing your creation. Please try again.');
    }
  };

  if (blockedFromPublishing) {
    return null;
  }

  return (
    <div className="add-creation">
      {socialNetworksPrompt.isOpen && (
        <SocialNetworksPrompt
          open={socialNetworksPrompt.isOpen}
          onClose={socialNetworksPrompt.closeModal}
        />
      )}
      {renderStepContent()}
      {currentStep !== 4 && <span onClick={() => !loading && handleClose()} className='fs-xxs fw-bold fg-dark td-underline d-flex jcc aic mb-24' disabled={loading}>CANCEL</span>
      }
    </div>
  );

}

export default AddCreation;